/**
 * SUNTRONIX ONE — Insights / Executive Pulse.
 *
 * Phase-1 shipped surface: 4 headline cards from the central Metric
 * Engine (`/api/insights/executive-pulse`), each clickable to the
 * appropriate drill-down route.  Filter bar visible but unused until
 * the metric engine wires date filters (Phase 2).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { GlobalFilterBar, MetricCard } from "./_shared";
import LifecycleSankey from "@/components/insights/LifecycleSankey";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ExecutivePulse() {
  const [pulse, setPulse] = useState(null);
  const [sankey, setSankey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ preset: "This Month" });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get("/insights/executive-pulse"),
        api.get("/insights/lifecycle-sankey"),
      ]);
      setPulse(p); setSankey(s);
    } catch { setPulse({ error: true }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="executive-pulse">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Insights</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Executive Pulse</h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Live headline metrics — every number is clickable and drills to the underlying records.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="pulse-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <GlobalFilterBar value={filter} onChange={setFilter} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <MetricCard metric={pulse?.technology_assets} drillTo="/assets" testId="pulse-technology-assets" tone="blue" />
        <MetricCard metric={pulse?.active_operations} drillTo="/insights/operations" testId="pulse-active-operations" tone="violet" />
        <MetricCard metric={pulse?.sla_compliance} drillTo="/insights/sla" testId="pulse-sla-compliance" tone="mint" />
        <MetricCard metric={pulse?.monthly_revenue} drillTo="/commercial/quotes" testId="pulse-monthly-revenue" tone="amber" />
      </div>

      <LifecycleSankey data={sankey} />

      <div className="text-[11px] text-slate-400" data-testid="pulse-freshness">
        {pulse?.updated_at
          ? `Data through ${new Date(pulse.updated_at).toLocaleString()}`
          : "Loading…"}
        
      </div>
    </div>
  );
}
